import React, { Component } from 'react';
import {
  select,
  scaleLinear,
  axisBottom,
  axisLeft,
  min,
  max,
  line,
  formatLocale,
  event
} from 'd3';

import StyledScatter from './StyledScatter';

class Scatter extends Component {

  state = {
    width: 0,
    height: 0,
    init: false
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
    this.updateDimensions();
  }

  componentWillUnmount() {
      window.removeEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate(prevProps, prevState) {
    const newData = this.props.data && this.props.data !== prevProps.data;
    const newDimensions = prevState.width !== this.state.width || prevState.height !== this.state.height;
    if (newData || newDimensions) this.updateChart();
  }

  updateDimensions = () => {
    if (!this.container) return;
    const { margin } = this.props;
    const width = this.container.clientWidth,
          height = this.props.height || this.container.clientHeight,
          chartWidth = width > 0 ? width - margin.left - margin.right : 0,
          chartHeight = height > 0 ? height - margin.top - margin.bottom : 0;

    const containerX = this.container.getBoundingClientRect().left;
    const containerY = this.container.getBoundingClientRect().top;

    if (this.state.width !== width || this.state.height !== height) this.setState({ width, height, chartWidth, chartHeight, containerX, containerY });
  }

  updateChart = () => {
    const props = this.props;
    const { data, domain, margin, colorPoints, colorAxis } = props;
    const { chartWidth, chartHeight, containerX, containerY } = this.state;

    const localeFormatter = formatLocale({
      "decimal": ",",
      "thousands": ".",
      "grouping": [3],
      "comma": true,
    });

    const svg = select(this.container).select("svg");

    const minX = domain.minX === undefined ? min(data, d => d.inkomen) : domain.minX;
    const maxX = domain.maxX === undefined ? max(data, d => d.inkomen) : domain.maxX;
    const minY = domain.minY === undefined ? min(data, d => d.koopkracht) : domain.minY;
    const maxY = domain.maxY === undefined ? max(data, d => d.koopkracht) : domain.maxY;

    const xScale = scaleLinear().range([0, chartWidth]).domain([minX,maxX]);
    const yScale = scaleLinear().range([chartHeight, 0]).domain([minY,maxY]);

    const formatX = props.formatX || localeFormatter.format(',.0f');

    const xAxis = axisBottom().scale(xScale).ticks(5).tickFormat(formatX);
    const yAxis = axisLeft().scale(yScale).ticks(5);

    svg.select(".af-scatter")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    svg.select(".x.axis")
      .attr("transform", "translate(0," + chartHeight + ")")
      .call(xAxis);

    svg.select(".y.axis")
        .call(yAxis)

    const dots = svg.select(".dots").selectAll(".dot").data(data);

    dots.exit().remove();

    dots
      .enter()
        .append("circle")
          .attr("class", "dot")
          .attr("r", 1)
          .style("fill", colorPoints)
          .style("stroke", colorPoints)
          .style("stroke-width", 0)
      .merge(dots)
        .attr("cx", d => xScale(+d.inkomen))
        .attr("cy", d => yScale(+d.koopkracht))

    const dataMedian1 = data.filter(d => d.werkenden);
    const dataMedian2 = data.filter(d => d.uitkeringsgerechtigden && d.uitkeringsgerechtigden !== "" && d.uitkeringsgerechtigden !== undefined);
    const dataMedian3 = data.filter(d => d.gepensioneerden && d.gepensioneerden !== "" && d.gepensioneerden !== undefined);

    const lineMedian1 = line()
      .x( d => xScale(+d.inkomen))
      .y( d => yScale(+d.werkenden))

    const lineMedian2 = line()
      .x( d => xScale(+d.inkomen))
      .y( d => yScale(+d.uitkeringsgerechtigden))

    const lineMedian3 = line()
      .x( d => xScale(+d.inkomen))
      .y( d => yScale(+d.gepensioneerden))

    svg.select(".af-line-werkenden")
      .datum(dataMedian1)
      .attr("d", lineMedian1);

    svg.select(".af-line-uitkeringsgerechtigden")
      .datum(dataMedian2)
      .attr("d", lineMedian2);

    svg.select(".af-line-gepensioneerden")
      .datum(dataMedian3)
      .attr("d", lineMedian3);

    const axis = svg.selectAll(".axis");
    axis.selectAll("path").style("stroke", colorAxis);
    axis.selectAll("line").style("stroke", colorAxis);
    axis.selectAll("text").style("fill", colorAxis);

    svg.select('.dots')
      .on('mousemove', (d) => {
        const data = event.target.__data__;
        const display = data.werkenden ? 'block' : 'none';
        let html = `
            <table><tbody>
            <tr><td>inkomen:</td><td>€${localeFormatter.format(',.0f')(data.inkomen)}</td></tr>
            <tr><td>werkenden:</td><td>${localeFormatter.format(',.1f')(data.werkenden)}%</td></tr>
            `;
        if (data.uitkeringsgerechtigden) html += `<tr><td>uitkeringsgerechtigden: </td><td>${localeFormatter.format(',.1f')(data.uitkeringsgerechtigden)}%</td></tr>`;
        if (data.gepensioneerden) html += `<tr><td>gepensioneerden: </td><td>${localeFormatter.format(',.1f')(data.gepensioneerden)}%</td></tr>`;
        html +=`</tbody></table>`
        select(this.container).select('.af-tooltip-scatter')
          .style('top', event.clientY - containerY + 12 + 'px')
          .style('left', event.clientX - containerX + 12 + 'px')
          .style('display', display)
          .html(html)
      });

    svg.select('.background').on('mouseleave', () => {
        select(this.container).select('.af-tooltip-scatter')
          .style('display', 'none');
      })

    this.setState({ init: true })

  }


  render() {
    const { width, height, chartWidth, chartHeight } = this.state;
    const { title, source, sourceUrl, labelX, labelY, unitX, margin, highlight } = this.props;

    const sourceCode = sourceUrl ? <a href={sourceUrl} target="_blank">{source}</a> : source;

    const opac = { opacity: 0.3 };
    const notOpac = { opacity: 1 }

    const dotStyle = highlight ? opac : notOpac;
    let median1Style = highlight ? opac : notOpac;
    let median2Style = highlight ? opac : notOpac;
    let median3Style = highlight ? opac : notOpac;


    if (highlight === 'highlight-scatter-werkenden') median1Style = notOpac;
    if (highlight === 'highlight-scatter-uitkeringsgerechtigden') median2Style = notOpac;
    if (highlight === 'highlight-scatter-gepensioneerden') median3Style = notOpac;

    return (
      <StyledScatter innerRef={container => this.container = container} >
        <h4 className="af-title">{title}</h4>
        <svg width={width} height={height} >
          <g className="af-scatter">
            <rect className="background"
              width={chartWidth}
              height={chartHeight}
              opacity={0}
            />
            <g className="dots"
              style={dotStyle}
            />
            <g className="lines"
              fill="none" stroke="#fff"
              strokeLinejoin="round"
              strokeLinecap="round"
              strokeWidth={3}>

              <path className="af-line-werkenden" style={median1Style} />
              <path className="af-line-uitkeringsgerechtigden" style={median2Style} />
              <path className="af-line-gepensioneerden" style={median3Style} />

            </g>
            <g className="x axis">
              <text x={width - margin.left - margin.right} textAnchor="start" dy="6" dx="9" >{labelX}</text>
              <text x={width - margin.left - margin.right} textAnchor="start" dy="20" dx="-5" >{unitX}</text>
            </g>
            <g className="y axis">
              <text dx="3" dy="-12" >{labelY}</text>
            </g>
          </g>
        </svg>
        <div className="af-source">{sourceCode}</div>
        <div className="af-tooltip-scatter" />
      </StyledScatter>
    );
  }
}

export default Scatter;



// WEBPACK FOOTER //
// ./src/components/Scatter/Scatter.js